<template>
    <Modal
        v-model="showModal"
        title="请求日志详情"
        width="1000"
        :mask-closable="false"
        class="request-log-detail-modal"
    >
        <div v-if="logData" class="log-detail">
            <!-- 第一行：基本信息与用户网络信息 (保持不变) -->
            <Row :gutter="16">
                <Col span="12">
                    <Card title="基本信息" :bordered="false" dis-hover class="info-card">
                        <div class="info-item">
                            <span class="label">请求ID：</span>
                            <span class="value">{{ logData.requestId || '-' }}</span>
                        </div>
                        <div class="info-item">
                            <span class="label">请求时间：</span>
                            <span class="value">{{ formatDateTime(logData.timestamp) }}</span>
                        </div>
                        <div class="info-item">
                            <span class="label">HTTP方法：</span>
                            <Tag :color="getMethodColor(logData.method)">{{ logData.method }}</Tag>
                        </div>
                        <div class="info-item">
                            <span class="label">路径：</span>
                            <Tooltip :content="logData.path" placement="top" max-width="300" transfer>
                                <span class="value path-value text-truncate">{{ logData.path || '-' }}</span>
                            </Tooltip>
                        </div>
                        <div class="info-item">
                            <span class="label">查询参数：</span>
                            <span class="value">{{ logData.queryString || '-' }}</span>
                        </div>
                    </Card>
                </Col>
                <Col span="12">
                    <Card title="用户与网络信息" :bordered="false" dis-hover class="info-card">
                        <div class="info-item">
                            <span class="label">用户：</span>
                            <span class="value">{{ logData.userName }} ({{ logData.userId || '-' }})</span>
                        </div>
                        <div class="info-item">
                            <span class="label">客户端IP：</span>
                            <span class="value">{{ logData.remoteIpAddress || '-' }}</span>
                        </div>
                        <div class="info-item">
                            <span class="label">状态码：</span>
                            <Tag :color="getStatusCodeColor(logData.responseStatusCode)">
                                {{ logData.responseStatusCode }}
                            </Tag>
                        </div>
                        <div class="info-item">
                            <span class="label">耗时：</span>
                            <span class="value" :style="{ color: getDurationColor(logData.duration), fontWeight: 'bold' }">
                                {{ logData.duration }}ms
                            </span>
                        </div>
                        <div class="info-item">
                            <span class="label">UA：</span>
                            <Tooltip :content="logData.userAgent" placement="top" max-width="300" transfer>
                                <span class="value user-agent text-truncate">{{ logData.userAgent || '-' }}</span>
                            </Tooltip>
                        </div>
                    </Card>
                </Col>
            </Row>

            <!-- 第二行：请求体与响应体 (核心修改部分) -->
            <Row :gutter="16" style="margin-top: 16px;">
                <!-- 请求信息 -->
                <Col span="12">
                    <Card :bordered="false" dis-hover class="body-card">
                        <div slot="title" class="card-header">
                            <span>请求信息</span>
                        </div>
                        <a slot="extra" href="javascript:void(0)" @click="showRequestBody = !showRequestBody">
                            {{ showRequestBody ? '收起' : '展开' }}
                            <Icon :type="showRequestBody ? 'ios-arrow-up' : 'ios-arrow-down'"></Icon>
                        </a>
                        
                        <!-- 常驻显示的元数据 -->
                        <div class="meta-info">
                            <Tag type="border">Size: {{ formatFileSize(logData.requestSize) }}</Tag>
                            <Tag type="border" color="blue" v-if="logData.contentType">{{ logData.contentType }}</Tag>
                        </div>

                        <!-- 可折叠区域 -->
                        <transition name="slide-fade">
                            <div v-show="showRequestBody" class="code-wrapper">
                                <div class="info-item" v-if="logData.ErrorMessage">
                                    <Alert type="error" show-icon>
                                        错误信息
                                        <span slot="desc">{{ logData.errorMessage }}</span>
                                    </Alert>
                                </div>
                                <div class="code-box">
                                    <pre>{{ formatJson(logData.requestBody) }}</pre>
                                </div>
                            </div>
                        </transition>
                    </Card>
                </Col>

                <!-- 响应信息 -->
                <Col span="12">
                    <Card :bordered="false" dis-hover class="body-card">
                        <div slot="title" class="card-header">
                            <span>响应信息</span>
                        </div>
                        <a slot="extra" href="javascript:void(0)" @click="showResponseBody = !showResponseBody">
                            {{ showResponseBody ? '收起' : '展开' }}
                            <Icon :type="showResponseBody ? 'ios-arrow-up' : 'ios-arrow-down'"></Icon>
                        </a>

                        <!-- 常驻显示的元数据 -->
                        <div class="meta-info">
                            <Tag type="border">Size: {{ formatFileSize(logData.responseSize) }}</Tag>
                            <Tag type="border" color="green" v-if="logData.responseContentType">{{ logData.responseContentType }}</Tag>
                        </div>

                        <!-- 可折叠区域 -->
                        <transition name="slide-fade">
                            <div v-show="showResponseBody" class="code-wrapper">
                                <div class="code-box">
                                    <pre>{{ formatJson(logData.responseBody) }}</pre>
                                </div>
                            </div>
                        </transition>
                    </Card>
                </Col>
            </Row>

            <!-- Curl命令部分 -->
            <Row v-if="logData && logData.curlCommand" style="margin-top: 16px;">
                <Col span="24">
                    <Card :bordered="false" dis-hover>
                        <p slot="title">Curl 命令</p>
                        <div class="curl-command-container">
                            <pre class="curl-command">{{ logData.curlCommand }}</pre>
                            <Button
                                type="primary"
                                size="small"
                                icon="md-copy"
                                @click="copyCurlCommand"
                                class="copy-btn">
                                复制
                            </Button>
                        </div>
                    </Card>
                </Col>
            </Row>

            <!-- 底部 Tabs (Header 信息) -->
            <Row v-if="requestHeaders || responseHeaders" style="margin-top: 16px;">
                <Col span="24">
                    <Card :bordered="false" dis-hover :padding="0">
                        <Tabs :animated="false">
                            <TabPane label="请求头" name="headers" v-if="requestHeaders">
                                <div class="tab-content-wrapper">
                                    <pre class="json-content">{{ formatJson(requestHeaders) }}</pre>
                                </div>
                            </TabPane>
                            <TabPane label="响应头" name="responseHeaders" v-if="responseHeaders">
                                <div class="tab-content-wrapper">
                                    <pre class="json-content">{{ formatJson(responseHeaders) }}</pre>
                                </div>
                            </TabPane>
                        </Tabs>
                    </Card>
                </Col>
            </Row>
        </div>
        <div slot="footer">
            <Button @click="closeModal">关闭</Button>
        </div>
    </Modal>
</template>

<script>
export default {
    name: "RequestLogDetailModal",
    data() {
        return {
            showModal: false,
            logData: null,
            requestHeaders: null,
            responseHeaders: null,
            // 控制展开/收缩的状态，默认true(展开)
            showRequestBody: true,
            showResponseBody: true
        };
    },
    methods: {
        open(logData) {
            this.logData = logData;
            // 重置默认状态为展开
            this.showRequestBody = true;
            this.showResponseBody = true;
            
            // 解析 Header 数据
            try {
                this.requestHeaders = logData.requestHeaders ? JSON.parse(logData.requestHeaders) : null;
            } catch (e) {
                this.requestHeaders = logData.requestHeaders;
            }
            try {
                this.responseHeaders = logData.responseHeaders ? JSON.parse(logData.responseHeaders) : null;
            } catch (e) {
                this.responseHeaders = logData.responseHeaders;
            }
            
            this.showModal = true;
        },
        closeModal() {
            this.showModal = false;
            // 延迟清空数据，防止模态框关闭动画时内容突然消失
            setTimeout(() => {
                this.logData = null;
                this.requestHeaders = null;
                this.responseHeaders = null;
            }, 300);
        },
        formatDateTime(timestamp) {
            if (!timestamp) return '-';
            // 处理时间戳或日期字符串
            const date = new Date(timestamp);
            if(isNaN(date.getTime())) return timestamp;
            
            const Y = date.getFullYear() + '-';
            const M = (date.getMonth() + 1 < 10 ? '0' + (date.getMonth() + 1) : date.getMonth() + 1) + '-';
            const D = (date.getDate() < 10 ? '0' + date.getDate() : date.getDate()) + ' ';
            const h = (date.getHours() < 10 ? '0' + date.getHours() : date.getHours()) + ':';
            const m = (date.getMinutes() < 10 ? '0' + date.getMinutes() : date.getMinutes()) + ':';
            const s = (date.getSeconds() < 10 ? '0' + date.getSeconds() : date.getSeconds());
            return Y + M + D + h + m + s;
        },
        getMethodColor(method) {
            const colorMap = {
                'GET': 'success', 'POST': 'primary', 'PUT': 'warning', 
                'DELETE': 'error', 'PATCH': 'magenta'
            };
            return colorMap[(method || '').toUpperCase()] || 'default';
        },
        getStatusCodeColor(code) {
            const c = parseInt(code);
            if (c >= 200 && c < 300) return 'success';
            if (c >= 300 && c < 400) return 'warning';
            if (c >= 400) return 'error';
            return 'default';
        },
        getDurationColor(duration) {
            if (duration > 5000) return '#ed4014'; // red
            if (duration > 1000) return '#ff9900'; // orange
            if (duration < 500) return '#19be6b';  // green
            return '#666';
        },
        formatFileSize(bytes) {
            if (!bytes || bytes === 0) return '0 B';
            const k = 1024;
            const sizes = ['B', 'KB', 'MB', 'GB'];
            const i = Math.floor(Math.log(bytes) / Math.log(k));
            return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
        },
        formatJson(data) {
            if (!data) return '';
            try {
                // 如果已经是对象直接格式化
                if (typeof data === 'object') {
                    return JSON.stringify(data, null, 4);
                }
                // 如果是字符串，尝试解析后再格式化
                const jsonObj = JSON.parse(data);
                return JSON.stringify(jsonObj, null, 4);
            } catch (e) {
                // 解析失败直接返回原文本
                return data;
            }
        },
        copyCurlCommand() {
            if (!this.logData || !this.logData.curlCommand) return;
            // 使用 Clipboard API (现代浏览器) 或 fallback
            if (navigator.clipboard) {
                navigator.clipboard.writeText(this.logData.curlCommand).then(() => {
                    this.$Message.success('已复制 Curl 命令');
                });
            } else {
                // Fallback
                const textarea = document.createElement('textarea');
                textarea.value = this.logData.curlCommand;
                document.body.appendChild(textarea);
                textarea.select();
                document.execCommand('copy');
                document.body.removeChild(textarea);
                this.$Message.success('已复制 Curl 命令');
            }
        }
    }
};
</script>

<style lang="less" scoped>
.request-log-detail-modal {
    /deep/ .ivu-modal-body {
        background-color: #f5f7f9;
        padding: 16px;
    }

    .log-detail {
        .info-card, .body-card {
            border-radius: 8px;
            box-shadow: 0 1px 2px rgba(0,0,0,0.05);
            height: 100%;
        }

        .info-item {
            margin-bottom: 10px;
            display: flex;
            align-items: flex-start;
            font-size: 13px;
            line-height: 1.5;
            
            &:last-child {
                margin-bottom: 0;
            }

            .label {
                font-weight: 500;
                color: #888;
                width: 80px;
                flex-shrink: 0;
                text-align: right;
                margin-right: 8px;
            }
            .value {
                color: #333;
                word-break: break-all;
                flex: 1;
            }
            .text-truncate {
                display: inline-block;
                max-width: 100%;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
                vertical-align: bottom;
            }
            .path-value {
                font-family: Consolas, 'Courier New', monospace;
                color: #2d8cf0;
                background: rgba(45, 140, 240, 0.05);
                padding: 0 4px;
                border-radius: 2px;
            }
        }

        /* 元数据标签行 */
        .meta-info {
            margin-bottom: 12px;
            padding-bottom: 12px;
            border-bottom: 1px dashed #e8e8e8;
            /deep/ .ivu-tag {
                margin-bottom: 0;
            }
        }

        /* 代码块区域 */
        .code-wrapper {
            margin-top: 8px;
        }

        .code-box {
            position: relative;
            background: #fafafa;
            border: 1px solid #e8e8e8;
            border-radius: 4px;
            
            pre {
                padding: 12px;
                margin: 0;
                font-family: Consolas, Monaco, 'Andale Mono', monospace;
                font-size: 12px;
                line-height: 1.5;
                color: #555;
                white-space: pre-wrap;
                word-wrap: break-word;
                max-height: 400px;
                overflow-y: auto;

                /* 美化滚动条 */
                &::-webkit-scrollbar {
                    width: 6px;
                    height: 6px;
                }
                &::-webkit-scrollbar-thumb {
                    background: #ddd;
                    border-radius: 3px;
                }
                &::-webkit-scrollbar-track {
                    background: transparent;
                }
            }
        }
    }

    .curl-command-container {
        position: relative;
        background: #2d2d2d; /* Dark theme for Curl */
        border-radius: 4px;
        
        .curl-command {
            padding: 16px;
            padding-right: 80px;
            margin: 0;
            color: #ccc;
            font-family: Consolas, monospace;
            font-size: 12px;
            white-space: pre-wrap;
            word-break: break-all;
            max-height: 200px;
            overflow-y: auto;
        }

        .copy-btn {
            position: absolute;
            top: 8px;
            right: 8px;
            opacity: 0.8;
            &:hover { opacity: 1; }
        }
    }

    .tab-content-wrapper {
        padding: 16px;
        background: #fff;
    }
    
    .json-content {
        margin: 0;
        font-family: Consolas, monospace;
        font-size: 12px;
        background: #f8f8f9;
        padding: 12px;
        border-radius: 4px;
        color: #515a6e;
    }

    /* 动画效果 */
    .slide-fade-enter-active, .slide-fade-leave-active {
        transition: all 0.3s ease;
        max-height: 500px; /* 近似最大高度，用于动画 */
        overflow: hidden;
        opacity: 1;
    }
    .slide-fade-enter, .slide-fade-leave-to {
        max-height: 0;
        opacity: 0;
        margin-top: 0;
        padding-top: 0;
        padding-bottom: 0;
    }
}
</style>